/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FilePlus2, Send, Info } from 'lucide-react';
import { getFseDocTypes, createFseDocument } from '@/lib/fseLombardia';

const DEFAULT_TYPE = 'referto';

export const DocumentCreator = ({ onCreated }) => {
  const [types, setTypes] = useState([]);
  const [docType, setDocType] = useState(DEFAULT_TYPE);
  const [patientId, setPatientId] = useState('');
  const [anamnesi, setAnamnesi] = useState('');
  const [esame, setEsame] = useState('');
  const [diagnosi, setDiagnosi] = useState('');
  const [terapia, setTerapia] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await getFseDocTypes(); setTypes(r.types || []); }
      catch { setTypes([]); }
    })();
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!patientId.trim()) { toast.error('Inserisci l\'ID paziente'); return; }
    if (!diagnosi.trim() && !anamnesi.trim() && !note.trim()) {
      toast.error('Almeno una sezione clinica deve essere compilata');
      return;
    }
    setBusy(true);
    try {
      const doc = await createFseDocument({
        patient_id: patientId.trim(),
        doc_type: docType,
        anamnesi, esame_obiettivo: esame, diagnosi, terapia, note,
      });
      toast.success('Documento CDA2 creato');
      onCreated?.(doc);
      // reset only clinical fields to allow batch creation
      setAnamnesi(''); setEsame(''); setDiagnosi(''); setTerapia(''); setNote('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nella creazione');
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <FilePlus2 className="h-4 w-4 text-primary" /> Nuovo documento CDA2
        </CardTitle>
        <CardDescription>Richiede consenso FSE attivo del paziente.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3" data-testid="fse-document-form">
          <div className="grid gap-3 sm:grid-cols-[1fr_260px]">
            <div>
              <Label className="text-xs">ID Paziente</Label>
              <Input
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                placeholder="UUID paziente (dal record clinico)"
                maxLength={64}
                data-testid="fse-patient-id-input"
              />
            </div>
            <div>
              <Label className="text-xs">Tipo documento</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="fse-doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.key} value={t.key} data-testid={`fse-type-${t.key}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {[
            { label: 'Anamnesi', value: anamnesi, set: setAnamnesi, key: 'anamnesi' },
            { label: 'Esame obiettivo', value: esame, set: setEsame, key: 'esame' },
            { label: 'Diagnosi', value: diagnosi, set: setDiagnosi, key: 'diagnosi' },
            { label: 'Terapia', value: terapia, set: setTerapia, key: 'terapia' },
            { label: 'Note / Follow-up', value: note, set: setNote, key: 'note' },
          ].map((s) => (
            <div key={s.key}>
              <Label className="text-xs">{s.label}</Label>
              <Textarea
                value={s.value}
                onChange={(e) => s.set(e.target.value.slice(0, 4000))}
                rows={2}
                placeholder={`Sezione: ${s.label}…`}
                data-testid={`fse-section-${s.key}`}
              />
            </div>
          ))}

          <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <Info className="h-3 w-3 mt-0.5" />
            <span>
              Il documento viene generato in formato HL7 CDA R2 (livello 2) con LOINC. La trasmissione a SISS è al momento <b>simulata</b> (mTLS) in attesa di accreditamento AgID.
            </span>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={busy} data-testid="fse-create-btn">
              <Send className="h-4 w-4 mr-1" /> Crea documento
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default DocumentCreator;
